import { axios, deleteUserCache, submitPickingSalesOrder } from 'api'
import {
  deleteOrders,
  getOrderBySONumber,
  getOrders,
  requireToCancelOrders
} from 'api/daily-order'
import { DailyOrderStatus, DailyOrderType, SnackbarSeverity } from 'constant'
import { Dispatch } from 'react'
import {
  getDailyOrderStatusLabel,
  playErrorAlertAudio,
  setElementToTop
} from 'utils'

/** Bin / location string compare: A–Z ascending, case-insensitive, numeric segments sensible. */
const compareBinAsc = (a: string, b: string) =>
  (a || '').localeCompare(b || '', undefined, {
    sensitivity: 'base',
    numeric: true
  })

const compareClassAsc = (a: string, b: string) =>
  (a || '').localeCompare(b || '', undefined, {
    sensitivity: 'base',
    numeric: true
  })

/**
 * Group label format: "Brake Pad + Brake Rotor" (class names joined in alphabetical order).
 * Sort orders by ascending bin among line items whose class matches the first name in that label.
 * If no lines match that class, fall back to the minimum bin across all lines on the order.
 */
const getPrimaryClassFromGroupLabel = (groupClassLabel: string) => {
  const parts = groupClassLabel
    .split(' + ')
    .map(s => s.trim())
    .filter(Boolean)
  return parts[0] || ''
}

const normalizeClassName = (c: unknown) =>
  (typeof c === 'string' ? c : String(c ?? '')).trim().toLowerCase()

const minBinAmongItems = (items: any[]): string => {
  let best = ''
  for (const it of items) {
    const b = (it?.bin ?? '').toString().trim()
    if (!b) continue
    if (!best || compareBinAsc(b, best) < 0) best = b
  }
  return best
}

const getOrderBinSortKeyForGroup = (order: any, groupClassLabel: string) => {
  const primary = getPrimaryClassFromGroupLabel(groupClassLabel)
  const pn = normalizeClassName(primary)
  const items = order.orderItems || []
  if (!pn) return minBinAmongItems(items)
  const sameClass = items.filter(
    (it: any) => normalizeClassName(it.class) === pn
  )
  return minBinAmongItems(sameClass.length > 0 ? sameClass : items)
}

const useDailyOrder = () => {
  const sortBoxCodes = (item: any) => {
    if (!Array.isArray(item.boxCodes) || item.boxCodes.length === 0) return
    const sorted = Array.from(
      new Set<string>(item.boxCodes.map((code: string) => `${code}`))
    )
      .filter((code: string) => code.trim().length > 0)
      .sort((a: string, b: string) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      )
    item.boxCodes = sorted
    item.box = sorted[0] || ''
  }

  const fetchDailyOrder = async (
    company: string,
    orderNumber: string,
    setSelectedDailyOrder: (order: any) => void,
    handleOpenSnackbar: (severity: SnackbarSeverity, message: string) => void
  ) => {
    try {
      const result = await getOrderBySONumber(`${company}`, `${orderNumber}`)
      if (result.data.success) {
        const order = result.data.order
        order.orderItems.forEach((item: any) => {
          item.quantityRemaining = item.quantity - item.quantityPicked
          item.quantityPicked = 0
          sortBoxCodes(item)
        })
        setSelectedDailyOrder(order)
      }
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        if (error.response.status === 409) {
          handleOpenSnackbar(
            SnackbarSeverity.Warning,
            'Conflict detected: The order has been modified elsewhere.'
          )
        } else {
          handleOpenSnackbar(
            SnackbarSeverity.Error,
            'Failed to fetch order data.'
          )
        }
      } else {
        console.error('Error fetching order:', error)
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          'An unexpected error occurred while fetching the order.'
        )
      }
    }
  }
  const fetchDailyOrders = async (
    company: string,
    dailyOrderType: DailyOrderType,
    boxPrefix?: string,
    skipBoxLookup?: boolean
  ): Promise<any[]> => {
    try {
      const result = await getOrders(
        `${company}`,
        dailyOrderType,
        boxPrefix,
        skipBoxLookup
      )
      const orders = result.data.orders
      orders.forEach((order: any) => {
        order.orderItems.forEach((item: any) => {
          item.quantityRemaining = item.quantity - item.quantityPicked
          item.quantityPicked = 0
          sortBoxCodes(item)
          if (dailyOrderType === DailyOrderType.BoxCodeSalesOrder) {
            if (item.bin) {
              item.rack = item.bin.split('-')[0]
            }
          }
        })
      })
      return orders
    } catch (error) {
      console.error('Error fetching orders:', error)
      return []
    }
  }

  const groupAndSortDailyOrdersByClass = (orders: any[]) => {
    const groupedOrdersMap: Record<string, any[]> = {}
    const isPicked = (order: any) => {
      return order.orderItems.every((item: any) => item.quantityRemaining === 0)
    }
    for (const order of orders) {
      const classSet = new Set(
        order.orderItems.map((item: any) => item.class).filter(Boolean)
      )
      if (classSet.size === 0) continue
      const sortedClasses = Array.from(classSet).sort((a: any, b: any) =>
        compareClassAsc(String(a ?? ''), String(b ?? ''))
      )
      const orderItemClass = sortedClasses.join(' + ')
      order.orderItems.sort((a: any, b: any) =>
        compareBinAsc((a.bin ?? '').toString(), (b.bin ?? '').toString())
      )
      if (!groupedOrdersMap[orderItemClass]) {
        groupedOrdersMap[orderItemClass] = []
      }
      groupedOrdersMap[orderItemClass].push(order)
    }
    const groupedOrdersArray = Object.entries(groupedOrdersMap)
      .map(([orderClass, orders]) => ({
        class: orderClass,
        orders: orders.sort((a, b) => {
          const aPicked = isPicked(a)
          const bPicked = isPicked(b)
          if (aPicked && !bPicked) return 1
          if (!aPicked && bPicked) return -1
          const binA = getOrderBinSortKeyForGroup(a, orderClass)
          const binB = getOrderBinSortKeyForGroup(b, orderClass)
          const binCmp = compareBinAsc(binA, binB)
          if (binCmp !== 0) return binCmp
          return (a.orderNumber || '').localeCompare(
            b.orderNumber || '',
            undefined,
            {
              sensitivity: 'base',
              numeric: true
            }
          )
        })
      }))
      .sort((a, b) => compareClassAsc(a.class, b.class))
    return groupedOrdersArray
  }

  const groupItemsByClassAndRack = (items: any[]) => {
    const mergedMap = new Map<string, any>()
    for (const item of items) {
      const key = `${item.name}-${item.class}-${item.rack}`
      if (!mergedMap.has(key)) {
        mergedMap.set(key, { ...item })
      } else {
        const existing = mergedMap.get(key)
        existing.quantity += item.quantity || 0
        existing.quantityPicked += item.quantityPicked || 0
        existing.quantityRemaining += item.quantityRemaining || 0
      }
    }
    const mergedItems = Array.from(mergedMap.values())
    const groupedByClass = mergedItems.reduce(
      (acc: Record<string, any[]>, item) => {
        const itemClass = item.class || 'Unknown'
        if (!acc[itemClass]) acc[itemClass] = []
        acc[itemClass].push(item)
        return acc
      },
      {}
    )
    return Object.entries(groupedByClass)
      .map(([className, classItems]) => {
        const groupedByRack = classItems.reduce(
          (rackAcc: Record<string, any[]>, item: any) => {
            const rack = item.rack || 'Unknown'
            if (!rackAcc[rack]) rackAcc[rack] = []
            rackAcc[rack].push(item)
            return rackAcc
          },
          {}
        )
        const racks = Object.entries(groupedByRack)
          .map(([rackName, items]) => ({
            rack: rackName,
            items: items
              .slice()
              .sort((a: any, b: any) =>
                compareBinAsc(
                  (a.bin ?? '').toString(),
                  (b.bin ?? '').toString()
                )
              )
          }))
          .sort((a, b) => compareBinAsc(a.rack.toString(), b.rack.toString()))

        return {
          class: className,
          racks
        }
      })
      .sort((a, b) => a.class.localeCompare(b.class))
  }

  const handleDeleteDailyOrders = async (
    company: string,
    ids: string[],
    setOrders: Dispatch<React.SetStateAction<any[]>>,
    selectionModel: any,
    setSelectionModel: (selectionModel: any) => void,
    handleOpenSnackbar: (severity: SnackbarSeverity, message: string) => void
  ) => {
    try {
      const selectedIds = Array.from(ids)
      const result = await deleteOrders(`${company}`, selectedIds)
      if (result.data.success) {
        setOrders(prevOrders =>
          prevOrders.filter(order => !selectionModel.ids.has(order.id))
        )
        setSelectionModel({ type: 'include', ids: new Set() })
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Orders deleted successfully'
        )
      }
    } catch (error) {
      console.error('Failed to delete orders:', error)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        'Failed to delete orders: ' + error
      )
    }
  }

  const handleRequireToCancelOrders = async (
    ids: string[],
    company: string,
    setOrders: Dispatch<React.SetStateAction<any[]>>,
    setSelectionModel: (selectionModel: any) => void,
    handleOpenSnackbar: (severity: SnackbarSeverity, message: string) => void
  ) => {
    try {
      const selectedIds = Array.from(ids)
      const result = await requireToCancelOrders(company, selectedIds)
      if (result.data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            selectedIds.includes(order.id)
              ? {
                  ...order,
                  status: getDailyOrderStatusLabel(
                    DailyOrderStatus.PendingCancelled
                  )
                }
              : order
          )
        )
        setSelectionModel({ type: 'include', ids: new Set() })
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Orders status updated successfully'
        )
      }
    } catch (error) {
      console.error('Failed to cancel orders:', error)
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        'Failed to cancel orders: ' + error
      )
    }
  }

  const filterDailyOrdersByClass = (
    orders: any[],
    itemClass: string,
    setFilteredDailyOrdersByClass: (orders: any[]) => void
  ) => {
    const result = orders.find(order => order.class === itemClass)
    if (result) {
      setFilteredDailyOrdersByClass(result.orders)
    }
  }
  const getDailyOrderBackColor = (order: any) => {
    const totalQuantity = order.orderItems.reduce(
      (acc: number, item: any) => acc + item.quantity,
      0
    )
    const totalRemaining = order.orderItems.reduce(
      (acc: number, item: any) => acc + item.quantityRemaining,
      0
    )
    if (totalRemaining === totalQuantity) {
      return 'rgba(252, 207, 207, 1)'
    }
    if (totalRemaining > 0 && totalRemaining < totalQuantity) {
      return 'rgba(255, 255, 187, 1)'
    }
    if (totalRemaining <= 0) {
      return 'rgba(217, 252, 210, 1)'
    }
    return '#ffffff'
  }

  const calculateOrdersLeft = (orders: any[]) => {
    const unpickedOrders = orders.filter(
      order =>
        order.orderItems.some(
          (item: any) => item.quantityPicked < item.quantityRemaining
        ) && order.orderItems.length > 0
    )
    return unpickedOrders.length
  }

  const scanDailyOrderItem = (
    scannedValue: string,
    selectedOrder: any,
    setScannedValue: (value: string) => void,
    setSelectedOrder: (order: any) => void,
    handleOpenSnackbar: (severity: SnackbarSeverity, message: string) => void
  ) => {
    const itemIndex = selectedOrder.orderItems.findIndex(
      (item: any) => item.upcCode === scannedValue || item.name === scannedValue
    )
    if (itemIndex !== -1) {
      const updatedOrder = { ...selectedOrder }
      const item = updatedOrder.orderItems[itemIndex]
      if (item.quantityRemaining <= 0) {
        playErrorAlertAudio()
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          'Item has already been picked or is out of stock!'
        )
        setScannedValue('')
      } else {
        setElementToTop(updatedOrder.orderItems, itemIndex)
        item.quantityPicked += 1
        item.quantityRemaining -= 1
        setSelectedOrder(updatedOrder)
        setScannedValue('')
      }
    } else {
      playErrorAlertAudio()
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        'Invalid UPC code or item name! Please try again.'
      )
      setScannedValue('')
    }
  }

  const submitPickingDailyOrder = async (
    setLoading: (loading: boolean) => void,
    order: any,
    company: string,
    handleOpenSnackbar: (severity: SnackbarSeverity, message: string) => void,
    navigate: any,
    returnPath?: string,
    onSuccess?: (orderNumber: string) => void
  ) => {
    setLoading(true)
    try {
      const result = await submitPickingSalesOrder(
        order.orderNumber,
        order.orderItems,
        order.locationId,
        company
      )
      if (result.data.success) {
        onSuccess?.(`${order.orderNumber}`)
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Order picked successfully'
        )
        if (returnPath) {
          navigate(returnPath)
        } else {
          navigate(-1)
        }
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.errorMessage || 'Unexpected error occurred'
      handleOpenSnackbar(SnackbarSeverity.Error, errorMessage)
    } finally {
      setLoading(false)
    }
  }
  const removeCurrentUserCache = async (
    orderNumber: string,
    company: string
  ) => {
    await deleteUserCache(orderNumber, company)
  }

  const getBoxItemBackgroundColor = (item: any) => {
    const qtyRemaining = Number(item.quantityRemaining) || 0
    const boxQty = Number(item.boxQuantity) || 0
    const quantityNeeded = qtyRemaining - boxQty
    if (qtyRemaining <= 0) return 'rgba(217, 252, 210, 1)'
    if (boxQty === 0) return 'rgba(252, 207, 207, 1)'
    if (quantityNeeded > 0) return 'rgba(255, 255, 187, 1)'
    return 'rgba(217, 252, 210, 1)'
  }

  return {
    fetchDailyOrder,
    fetchDailyOrders,
    groupAndSortDailyOrdersByClass,
    scanDailyOrderItem,
    submitPickingDailyOrder,
    filterDailyOrdersByClass,
    getDailyOrderBackColor,
    calculateOrdersLeft,
    removeCurrentUserCache,
    handleDeleteDailyOrders,
    groupItemsByClassAndRack,
    getBoxItemBackgroundColor,
    handleRequireToCancelOrders
  }
}

export default useDailyOrder
